import type { Immutable } from "immer";
import React from "react";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";
import { useFlickity } from "tsi-common-react/src/hooks/useFlickity";
import { usePageSetting } from "tsi-common-react/src/utils/settings";
import { t } from "ttag";

import type { SizeGuideBlock as SizeGuideBlockValue } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import {
    BedCalKing,
    BedFullDouble,
    BedKing,
    BedQueen,
    BedSplitCaKing,
    BedSplitKing,
    BedTwin,
    BedTwinXl,
} from "../../blocks/SizeGuideBlock/svg";

/**
 * Native React port of the legacy server-rendered
 * ``cms/blocks/size-guide-grid.html`` (#33285 / #33457).
 *
 * The eight bed-size SVGs are inlined as JSX (transcribed from the svgo-built
 * ``static/dist2/svg-django/bed-*.svg`` sprites the legacy ``svg.html`` include
 * rendered -- the EXACT source the screenshot baseline was generated from).
 * Inline (not ``react-inlinesvg``) because react-inlinesvg fetches the SVG
 * client-side on mount (requires ``window``), so the icons would be ABSENT from
 * the SSR/fragment HTML and only appear after hydrate + async fetch -- a real
 * SSR regression for an SSR-V2 block (the ``TempurFinancePageBody`` /
 * ``WebpageIcon`` in-repo precedent inlines its ``svg.html`` icons for exactly
 * this reason). Each icon emits the ``cms/components/svg.html`` markup: a
 * screenreader ``<span class="ada-screenreader-only">{description}</span>``
 * SIBLING BEFORE the inline ``<svg className=icon--* role="img">`` (which stays
 * a DIRECT child of ``.icon`` so ``.icon svg { flex-grow: 1 }`` applies), and a
 * ``<title>`` element from the legacy ``title`` include arg.
 *
 * The ``APP_SLUG == 'tempurca'`` column conditional is reproduced via
 * ``usePageSetting('app_slug')`` (the established tempurca self-gate pattern;
 * the tempurca grid puts King + Split King in column 3, the default grid puts
 * King + Cal. King in column 3 and Split King + Split CA King in column 4).
 *
 * Richtext fields (``header_content`` + the four ``*_set_copy`` fields)
 * serialize through the default path ALREADY ``.rich-text``-wrapped
 * (``<div class="rich-text">...</div>``), matching the legacy
 * ``{% include_block %}`` output. The live SCSS targets these with CHILD
 * combinators (``.size-guide-block > .rich-text`` and ``.best-for >
 * .rich-text``), so each ``.rich-text`` MUST be a direct child of its
 * container. We strip the serialized wrapper and re-emit via
 * ``<RichTextBlock className="rich-text">`` (one ``.rich-text`` div, direct
 * child -- not the double wrapper a wrapping ``<div>`` would create). The
 * legacy empty guard ``value.<field>.source != '<p></p>'`` maps to: skip the
 * field iff the serialized value is the empty-paragraph render
 * ``<div class="rich-text"><p></p></div>`` (the only case the legacy skipped;
 * a missing/blank field serializes to ``<div class="rich-text"></div>`` and the
 * legacy RENDERED that empty div).
 *
 * Per-size ``-callout`` spans are filtered from ``bed_callouts`` by ``size``.
 * NOTE the preserved legacy quirk: the Split CA King callout compares against
 * ``'split cal. king'``, which never matches the ``'split ca king'`` choice
 * value -- a pre-existing bug, kept faithful (so that callout never renders).
 */

type IProps = Immutable<{ value: SizeGuideBlockValue }>;
type Value = IProps["value"];
type Callout = NonNullable<Value["bed_callouts"]>[number];

// The serialized render of an empty-paragraph richtext field. The legacy
// template guarded `value.<field>.source != '<p></p>'`; client-side the only
// value that the legacy skipped serializes to this exact string.
const EMPTY_RICHTEXT = '<div class="rich-text"><p></p></div>';
const RICHTEXT_PREFIX = '<div class="rich-text">';
const RICHTEXT_SUFFIX = "</div>";

// The richtext fields serialize ALREADY wrapped in `<div class="rich-text">`.
// Strip that known wrapper so `<RichTextBlock className="rich-text">` re-emits a
// single `.rich-text` div as a direct child (the SCSS child combinators require
// it). Returns null when the legacy template would have skipped the field.
const stripRichTextWrapper = (
    value: string | null | undefined,
): string | null => {
    const html = value ?? "";
    if (html === EMPTY_RICHTEXT) {
        return null;
    }
    if (html.startsWith(RICHTEXT_PREFIX) && html.endsWith(RICHTEXT_SUFFIX)) {
        return html.slice(RICHTEXT_PREFIX.length, -RICHTEXT_SUFFIX.length);
    }
    return html;
};

// SVG component signature shared by the bed-size icons.
type SvgComponent = (
    props: Immutable<{ title: string; className: string }>,
) => React.JSX.Element;

type IconProps = Immutable<{
    label: string;
    // Screenreader description (legacy svg.html `description` arg -> the
    // `.ada-screenreader-only` span before the inline svg).
    description: string;
    // The bed-size SVG component + its `<title>` text and root `icon--*` class.
    Svg: SvgComponent;
    title: string;
    iconClassName: string;
    dimensions: string;
    // Extra span (e.g. "2 Twin XLs") rendered between the dimensions text and
    // any callouts, matching the split-size legacy markup.
    extra?: string;
    // The bed_callouts entry size key whose callouts render in this icon.
    calloutSize: string;
    callouts: readonly Callout[];
}>;

const Icon = (props: IconProps): React.JSX.Element => {
    const Svg = props.Svg;
    return (
        <div className="icon">
            <p>{props.label}</p>
            <span className="ada-screenreader-only">{props.description}</span>
            <Svg title={props.title} className={props.iconClassName} />
            <p>
                {props.dimensions}
                {props.extra ? <span>{props.extra}</span> : null}
                {props.callouts
                    .filter((callout) => callout.size === props.calloutSize)
                    .map((callout, idx) => (
                        <span key={idx} className="-callout">
                            {callout.callout}
                        </span>
                    ))}
            </p>
        </div>
    );
};

const BestFor = (
    props: Immutable<{ value: string | null | undefined }>,
): React.JSX.Element | null => {
    const inner = stripRichTextWrapper(props.value);
    if (inner === null) {
        return null;
    }
    return (
        <div className="best-for">
            <p>{t`Best for:`}</p>
            <RichTextBlock value={inner} className="rich-text" />
        </div>
    );
};

export const SizeGuideBlock = (props: IProps): React.JSX.Element => {
    const v = props.value;
    const appSlug = usePageSetting("app_slug") || "";
    const isTempurCa = appSlug === "tempurca";
    const callouts = v.bed_callouts ?? [];
    const header = stripRichTextWrapper(v.header_content);

    // Flickity carousel on the size-guide `.grid`; watchCSS gates activation to
    // the mobile breakpoint (note `groupCells: true`).
    const gridRef = React.useRef<HTMLDivElement>(null);
    useFlickity(gridRef, {
        options: {
            watchCSS: true,
            draggable: true,
            wrapAround: true,
            prevNextButtons: false,
            pageDots: true,
            groupCells: true,
        },
    });

    return (
        <section
            className={concatClassNames([
                "size-guide-block",
                getGlobalSpacingClasses(v.spacing),
            ])}
        >
            {header !== null ? (
                <RichTextBlock value={header} className="rich-text" />
            ) : null}

            <div>{t`Mattress Size Guide`}</div>

            <div
                id="mattress-size-guide"
                className={concatClassNames([
                    "grid",
                    isTempurCa ? "grid-ca" : "",
                ])}
                ref={gridRef}
            >
                <div className="col" role="group" aria-label="Twin vs. Twin XL">
                    <Icon
                        label={t`Twin`}
                        description={t`bed – twin'`}
                        Svg={BedTwin}
                        title={t`Twin Bed`}
                        iconClassName="icon--twin"
                        dimensions={t`38" x 74"`}
                        calloutSize="twin"
                        callouts={callouts}
                    />
                    <Icon
                        label={t`Twin XL`}
                        description={t`bed – twin xl`}
                        Svg={BedTwinXl}
                        title={t`Extra Large Twin Bed`}
                        iconClassName="icon--twin-xl"
                        dimensions={t`38" x 80"`}
                        calloutSize="twin xl"
                        callouts={callouts}
                    />
                    <BestFor value={v.first_set_copy} />
                </div>

                <div
                    className="col"
                    role="group"
                    aria-label="Full/Double vs. Queen"
                >
                    <Icon
                        label={t`Full / Double`}
                        description={t`bed – full/double`}
                        Svg={BedFullDouble}
                        title={t`Full/Double Bed`}
                        iconClassName="icon--full"
                        dimensions={t`53" x 74"`}
                        calloutSize="full/double"
                        callouts={callouts}
                    />
                    <Icon
                        label={t`Queen`}
                        description={t`bed – queen`}
                        Svg={BedQueen}
                        title={t`Queen Bed`}
                        iconClassName="icon--queen"
                        dimensions={t`60" x 80"`}
                        calloutSize="queen"
                        callouts={callouts}
                    />
                    <BestFor value={v.second_set_copy} />
                </div>

                {isTempurCa ? (
                    <div
                        className="col"
                        role="group"
                        aria-label="King vs. Split King"
                    >
                        <Icon
                            label={t`King`}
                            description={t`bed – king`}
                            Svg={BedKing}
                            title={t`King Bed`}
                            iconClassName="icon--king"
                            dimensions={t`76" x 80"`}
                            calloutSize="king"
                            callouts={callouts}
                        />
                        <Icon
                            label={t`Split King`}
                            description={t`bed – split king`}
                            Svg={BedSplitKing}
                            title={t`Split King Bed`}
                            iconClassName="icon--split-king"
                            dimensions={t`76" x 80"`}
                            extra={t`2 Twin XLs`}
                            calloutSize="split king"
                            callouts={callouts}
                        />
                        <BestFor value={v.third_set_copy} />
                    </div>
                ) : (
                    <>
                        <div
                            className="col"
                            role="group"
                            aria-label="King vs. Cal. King"
                        >
                            <Icon
                                label={t`King`}
                                description={t`bed – king`}
                                Svg={BedKing}
                                title={t`King Bed`}
                                iconClassName="icon--king"
                                dimensions={t`76" x 80"`}
                                calloutSize="king"
                                callouts={callouts}
                            />
                            <Icon
                                label={t`Cal. King`}
                                description={t`bed – cal king`}
                                Svg={BedCalKing}
                                title={t`California King Bed`}
                                iconClassName="icon--cal-king"
                                dimensions={t`72" x 84"`}
                                calloutSize="cal. king"
                                callouts={callouts}
                            />
                            <BestFor value={v.third_set_copy} />
                        </div>
                        <div
                            className="col"
                            role="group"
                            aria-label="Split King vs. Split CA King"
                        >
                            <Icon
                                label={t`Split King`}
                                description={t`bed – split king`}
                                Svg={BedSplitKing}
                                title={t`Split King Bed`}
                                iconClassName="icon--split-king"
                                dimensions={t`76" x 80"`}
                                extra={t`2 Twin XLs`}
                                calloutSize="split king"
                                callouts={callouts}
                            />
                            <Icon
                                label={t`Split CA King`}
                                description={t`bed – split ca king`}
                                Svg={BedSplitCaKing}
                                title={t`Split California King Bed`}
                                iconClassName="icon--split-ca-king"
                                dimensions={t`72" x 84"`}
                                extra={t`36"(2) x 84"`}
                                // Preserved legacy quirk: never matches the
                                // 'split ca king' choice value (pre-existing bug).
                                calloutSize="split cal. king"
                                callouts={callouts}
                            />
                            <BestFor value={v.fourth_set_copy} />
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};
